import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2, Plus, Trash2, KeyRound, Webhook, Copy } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import CredencialRevelada from "../CredencialRevelada";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const BASE_URL = "https://talk.npsolucoesinfo.com.br/api/external/v1";

// Painel focado do NP Connect — a integração de mensageria do MSP (e de
// qualquer sistema externo). Só o essencial da integração: o token (amarrado a
// um canal e uma fila) e o webhook para receber. É a mesma API por trás do
// "API" geral do painel, mas aqui a tela é enxuta de propósito, para não
// confundir nem induzir a erro.
const NpConnectPanel = () => {
	const [tokens, setTokens] = useState([]);
	const [canais, setCanais] = useState([]);
	const [filas, setFilas] = useState([]);
	const [webhooks, setWebhooks] = useState([]);
	const [eventos, setEventos] = useState([]);
	const [carregando, setCarregando] = useState(true);

	const [novoToken, setNovoToken] = useState({ name: "", channelId: "", queueId: "" });
	const [salvandoToken, setSalvandoToken] = useState(false);
	const [tokenRevelado, setTokenRevelado] = useState(null);

	const [novoHook, setNovoHook] = useState({ url: "", events: [] });
	const [salvandoHook, setSalvandoHook] = useState(false);
	const [segredoRevelado, setSegredoRevelado] = useState(null);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const [tk, cx, fl, wh, ev] = await Promise.all([
				api.get("/api-tokens"),
				api.get("/whatsapp"),
				api.get("/queue"),
				api.get("/webhooks"),
				api.get("/webhooks/events"),
			]);
			setTokens(tk.data.tokens || []);
			setCanais(Array.isArray(cx.data) ? cx.data : []);
			setFilas(Array.isArray(fl.data) ? fl.data : []);
			setWebhooks(Array.isArray(wh.data) ? wh.data : wh.data.webhooks || []);
			setEventos(Array.isArray(ev.data) ? ev.data : ev.data.eventos || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const nome = (lista, id) => lista.find((x) => String(x.id) === String(id))?.name;

	const gerarToken = async (e) => {
		e.preventDefault();
		if (!novoToken.channelId || !novoToken.queueId) {
			toast.error("Escolha o canal e a fila do token.");
			return;
		}
		setSalvandoToken(true);
		try {
			const { data } = await api.post("/api-tokens", novoToken);
			setNovoToken({ name: "", channelId: "", queueId: "" });
			setTokenRevelado(data.valor);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvandoToken(false);
	};

	const revogarToken = async (t) => {
		if (!window.confirm(`Revogar "${t.name}"? A integração para de funcionar na hora.`)) return;
		try {
			await api.post(`/api-tokens/${t.id}/revoke`);
			await carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const alternarEvento = (chave) =>
		setNovoHook((h) => ({
			...h,
			events: h.events.includes(chave)
				? h.events.filter((c) => c !== chave)
				: [...h.events, chave],
		}));

	const criarWebhook = async (e) => {
		e.preventDefault();
		if (!novoHook.url || novoHook.events.length === 0) {
			toast.error("Informe a URL e ao menos um evento.");
			return;
		}
		setSalvandoHook(true);
		try {
			const { data } = await api.post("/webhooks", novoHook);
			setNovoHook({ url: "", events: [] });
			if (data.secret) setSegredoRevelado(data.secret);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvandoHook(false);
	};

	const excluirWebhook = async (w) => {
		if (!window.confirm("Excluir este webhook? Para de receber os eventos dele.")) return;
		try {
			await api.delete(`/webhooks/${w.id}`);
			await carregar();
		} catch (err) {
			toastError(err);
		}
	};

	if (carregando) {
		return (
			<div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
				<Loader2 className="h-4 w-4 animate-spin" /> Carregando…
			</div>
		);
	}

	const tokensAtivos = tokens.filter((t) => !t.revokedAt);

	return (
		<div className="space-y-8">
			{/* Endereço */}
			<div className="rounded-lg border bg-muted/40 p-4">
				<p className="text-sm">
					Endereço da API do NP Connect. Toda chamada leva{" "}
					<code className="rounded bg-background px-1 py-0.5 text-xs">
						Authorization: Bearer &lt;token&gt;
					</code>
					.
				</p>
				<div className="mt-2 flex items-center gap-2">
					<Input readOnly value={BASE_URL} className="font-mono text-xs" />
					<Button
						type="button"
						variant="outline"
						size="icon"
						title="Copiar"
						onClick={() => {
							navigator.clipboard.writeText(BASE_URL);
							toast.success("Endereço copiado");
						}}
					>
						<Copy className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Token */}
			<section className="space-y-4">
				<div className="flex items-center gap-2">
					<KeyRound className="h-4 w-4 text-primary" />
					<h3 className="text-base font-semibold">Token de acesso</h3>
				</div>
				<p className="text-sm text-muted-foreground">
					Cada token já sai amarrado a um <strong>canal</strong> (por qual número
					envia) e a uma <strong>fila</strong> (onde as conversas entram) — a
					integração não precisa escolher a cada chamada.
				</p>

				<form onSubmit={gerarToken} className="grid gap-3 rounded-lg border p-4 sm:grid-cols-2">
					<div className="sm:col-span-2">
						<Label htmlFor="np-nome">Nome da integração</Label>
						<Input
							id="np-nome"
							required
							minLength={2}
							placeholder="MSP (opendesk-helpdesk)"
							value={novoToken.name}
							onChange={(e) => setNovoToken({ ...novoToken, name: e.target.value })}
						/>
					</div>
					<div>
						<Label htmlFor="np-canal">Canal (conexão)</Label>
						<Select
							value={novoToken.channelId}
							onValueChange={(v) => setNovoToken({ ...novoToken, channelId: v })}
						>
							<SelectTrigger id="np-canal">
								<SelectValue placeholder="Escolha a conexão" />
							</SelectTrigger>
							<SelectContent>
								{canais.map((c) => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.name} {c.number ? `— ${c.number}` : ""}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label htmlFor="np-fila">Fila</Label>
						<Select
							value={novoToken.queueId}
							onValueChange={(v) => setNovoToken({ ...novoToken, queueId: v })}
						>
							<SelectTrigger id="np-fila">
								<SelectValue placeholder="Escolha a fila" />
							</SelectTrigger>
							<SelectContent>
								{filas.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										{f.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="sm:col-span-2">
						<Button type="submit" disabled={salvandoToken}>
							{salvandoToken ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Plus className="h-4 w-4" />
							)}
							Gerar token
						</Button>
					</div>
				</form>

				{tokensAtivos.length > 0 && (
					<div className="divide-y rounded-lg border">
						{tokensAtivos.map((t) => (
							<div key={t.id} className="flex items-center justify-between gap-3 p-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-medium">{t.name}</p>
									<p className="truncate text-xs text-muted-foreground">
										<code>{t.prefix}…</code> · canal:{" "}
										{nome(canais, t.channelId) || "—"} · fila:{" "}
										{nome(filas, t.queueId) || "—"} ·{" "}
										{t.lastUsedAt ? "em uso" : "nunca usado"}
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									title="Revogar"
									onClick={() => revogarToken(t)}
								>
									<Trash2 className="h-4 w-4 text-destructive" />
								</Button>
							</div>
						))}
					</div>
				)}
			</section>

			{/* Webhook */}
			<section className="space-y-4">
				<div className="flex items-center gap-2">
					<Webhook className="h-4 w-4 text-primary" />
					<h3 className="text-base font-semibold">Receber eventos (webhook)</h3>
				</div>
				<p className="text-sm text-muted-foreground">
					Para o sistema receber mensagem/atualização na hora, sem ficar
					consultando. Cada entrega vem assinada (HMAC) — o segredo aparece uma vez.
				</p>

				<form onSubmit={criarWebhook} className="space-y-3 rounded-lg border p-4">
					<div>
						<Label htmlFor="np-url">URL que vai receber</Label>
						<Input
							id="np-url"
							type="url"
							required
							placeholder="https://seu-sistema/webhook/whatsapp"
							value={novoHook.url}
							onChange={(e) => setNovoHook({ ...novoHook, url: e.target.value })}
						/>
					</div>
					<div>
						<Label>Eventos</Label>
						<div className="mt-1 flex flex-wrap gap-2">
							{eventos.map((ev) => {
								const marcado = novoHook.events.includes(ev.chave);
								return (
									<button
										type="button"
										key={ev.chave}
										onClick={() => alternarEvento(ev.chave)}
										className={`rounded-full border px-3 py-1 text-xs transition-colors ${
											marcado
												? "border-primary bg-primary/10 text-primary"
												: "text-muted-foreground hover:bg-muted"
										}`}
									>
										{ev.rotulo}
									</button>
								);
							})}
						</div>
					</div>
					<Button type="submit" disabled={salvandoHook}>
						{salvandoHook ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Plus className="h-4 w-4" />
						)}
						Criar webhook
					</Button>
				</form>

				{webhooks.length > 0 && (
					<div className="divide-y rounded-lg border">
						{webhooks.map((w) => (
							<div key={w.id} className="flex items-center justify-between gap-3 p-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-medium">{w.url}</p>
									<p className="truncate text-xs text-muted-foreground">
										{(w.events || []).join(", ") || "sem eventos"}
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									title="Excluir"
									onClick={() => excluirWebhook(w)}
								>
									<Trash2 className="h-4 w-4 text-destructive" />
								</Button>
							</div>
						))}
					</div>
				)}
			</section>

			<CredencialRevelada
				aberto={Boolean(tokenRevelado)}
				titulo="Token gerado"
				valor={tokenRevelado || ""}
				descricao="Guarde agora — não aparece de novo. Use no cabeçalho Authorization: Bearer."
				aoFechar={() => setTokenRevelado(null)}
			/>
			<CredencialRevelada
				aberto={Boolean(segredoRevelado)}
				titulo="Segredo do webhook"
				valor={segredoRevelado || ""}
				descricao="Use para conferir a assinatura HMAC (X-Webhook-Signature). Não aparece de novo."
				aoFechar={() => setSegredoRevelado(null)}
			/>
		</div>
	);
};

export default NpConnectPanel;
